import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { createInventoryItem, getAllProducts } from "../services/api";
import toast from "react-hot-toast";

export default function AddInventory() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    productSkuId: "",
    location: "",
    batch: "",
    quantity: "",
    minStock: "",
    expiryDate: "",
    itemDescription: "",
    supplierName: "",
    supplierCode: "",
    supplierContact: "",
    unitCost: "",
    storageConditions: "",
    qualityStatus: "",
    notes: "",
  });

  const [activeTab, setActiveTab] = useState("sku");
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [search, setSearch] = useState("");

  const tabs = [
    { id: "sku", label: "SKU Selection", icon: "🏷️" },
    { id: "basic", label: "Inventory Info", icon: "📦" },
    { id: "supplier", label: "Supplier", icon: "🏢" },
    { id: "details", label: "Details", icon: "📋" },
  ];

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const data = await getAllProducts();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Flatten products and their SKUs for selection
  const flattenedProducts = products.flatMap((product) =>
    product.skus
      ? product.skus.map((sku) => ({
          ...sku,
          productName: product.name,
          productItemCode: product.itemCode,
          displayName: `${product.name} - ${sku.sku} (${
            sku.barcode || "No barcode"
          })`,
        }))
      : []
  );

  const filteredProducts = flattenedProducts.filter(
    (p) =>
      p.displayName.toLowerCase().includes(search.toLowerCase()) ||
      p.sku.toLowerCase().includes(search.toLowerCase()) ||
      (p.barcode && p.barcode.toLowerCase().includes(search.toLowerCase())) ||
      p.productName.toLowerCase().includes(search.toLowerCase()) ||
      (p.productItemCode &&
        p.productItemCode.toLowerCase().includes(search.toLowerCase()))
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.productSkuId) {
      errors.productSkuId = "Product SKU is required";
    }

    if (!form.quantity || isNaN(form.quantity) || Number(form.quantity) <= 0) {
      errors.quantity = "Quantity must be a positive number";
    }

    if (form.minStock && (isNaN(form.minStock) || Number(form.minStock) < 0)) {
      errors.minStock = "Minimum stock must be a positive number";
    }

    if (form.unitCost && (isNaN(form.unitCost) || Number(form.unitCost) < 0)) {
      errors.unitCost = "Unit cost must be a positive number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setSubmitting(true);

      // Handle expiry date properly
      let expiryDate = null;
      if (form.expiryDate && form.expiryDate.trim() !== "") {
        const date = new Date(form.expiryDate);
        if (!isNaN(date.getTime())) {
          expiryDate = date.toISOString();
        }
      }

      const inventoryData = {
        productSkuId: form.productSkuId,
        location: form.location || null,
        batch: form.batch || null,
        quantity: Number(form.quantity),
        minStock: form.minStock ? Number(form.minStock) : null,
        expiryDate: expiryDate,
        itemDescription: form.itemDescription || null,
        supplierName: form.supplierName || null,
        supplierCode: form.supplierCode || null,
        supplierContact: form.supplierContact || null,
        unitCost: form.unitCost ? Number(form.unitCost) : null,
        storageConditions: form.storageConditions || null,
        qualityStatus: form.qualityStatus || null,
        notes: form.notes || null,
      };

      await createInventoryItem(inventoryData);
      toast.success("Inventory item created successfully!");
      navigate("/admin/inventory");
    } catch (error) {
      console.error("Error creating inventory item:", error);
      setError(error.message || "Failed to create inventory item");
      toast.error(error.message || "Failed to create inventory item");
    } finally {
      setSubmitting(false);
    }
  };

  const getInputClassName = (fieldName) => {
    const baseClass =
      "w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent";
    return validationErrors[fieldName]
      ? `${baseClass} border-red-500`
      : `${baseClass} border-gray-300`;
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4">
      {/* Back Button */}
      <button
        onClick={() => navigate("/admin/inventory")}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Inventory
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Add Inventory Item</h1>
        <p className="mt-2 text-gray-600">
          Add a new item to your inventory with detailed information
        </p>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Tab Navigation */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="flex space-x-1 p-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-white text-blue-600 shadow-sm border border-blue-200"
                    : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 max-h-[70vh] overflow-y-auto"
        >
          {/* SKU Selection Tab */}
          {activeTab === "sku" && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-blue-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-blue-700 font-medium">
                    SKU-Based Inventory Management
                  </span>
                </div>
                <p className="text-blue-600 text-sm mt-1">
                  Select the specific SKU variant for this inventory item. All
                  inventory operations are based on SKU-level tracking.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select SKU *
                </label>
                <input
                  type="text"
                  className={getInputClassName("productSkuId")}
                  placeholder="Search by SKU, barcode, product name, or product code"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {validationErrors.productSkuId && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.productSkuId}
                  </p>
                )}

                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg mt-2 bg-white">
                  {loadingProducts ? (
                    <div className="px-3 py-4 text-center text-gray-500">
                      Loading products...
                    </div>
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((p) => (
                      <div
                        key={p.id}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                          form.productSkuId === p.id
                            ? "bg-blue-100 text-blue-700"
                            : ""
                        }`}
                        onClick={() => {
                          handleChange("productSkuId", p.id);
                          setSearch(p.displayName);
                        }}
                      >
                        <div className="font-medium">{p.displayName}</div>
                        <div className="text-xs text-gray-500">
                          {p.productItemCode &&
                            `Product Code: ${p.productItemCode}`}
                          {p.sku && ` • SKU: ${p.sku}`}
                          {p.barcode && ` • Barcode: ${p.barcode}`}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-gray-400">
                      No SKUs found
                    </div>
                  )}
                </div>
              </div>

              {form.productSkuId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-700 mb-2">
                    ✓ Selected SKU Information
                  </h4>
                  {(() => {
                    const selectedSku = flattenedProducts.find(
                      (p) => p.id === form.productSkuId
                    );
                    if (!selectedSku)
                      return (
                        <div className="text-gray-500">No SKU selected</div>
                      );

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Product Name:</span>
                          <span className="ml-2 font-medium">
                            {selectedSku.productName}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Product Code:</span>
                          <span className="ml-2 font-medium">
                            {selectedSku.productItemCode}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">SKU Code:</span>
                          <span className="ml-2 font-medium">
                            {selectedSku.sku}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Barcode:</span>
                          <span className="ml-2 font-medium">
                            {selectedSku.barcode || "N/A"}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Basic Info Tab */}
          {activeTab === "basic" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    className={getInputClassName("quantity")}
                    value={form.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    placeholder="0"
                    min={1}
                  />
                  {validationErrors.quantity && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.quantity}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Min Stock Level
                  </label>
                  <input
                    type="number"
                    className={getInputClassName("minStock")}
                    value={form.minStock}
                    onChange={(e) => handleChange("minStock", e.target.value)}
                    placeholder="0"
                    min={0}
                  />
                  {validationErrors.minStock && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.minStock}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Unit Cost
                  </label>
                  <input
                    type="number"
                    className={getInputClassName("unitCost")}
                    value={form.unitCost}
                    onChange={(e) => handleChange("unitCost", e.target.value)}
                    placeholder="0.00"
                    min={0}
                    step={0.01}
                  />
                  {validationErrors.unitCost && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.unitCost}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="e.g., Warehouse A, Shelf B1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.batch}
                    onChange={(e) => handleChange("batch", e.target.value)}
                    placeholder="e.g., BATCH-2024-001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.expiryDate}
                    onChange={(e) => handleChange("expiryDate", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quality Status
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.qualityStatus}
                    onChange={(e) =>
                      handleChange("qualityStatus", e.target.value)
                    }
                  >
                    <option value="">Select status</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending Inspection</option>
                    <option value="rejected">Rejected</option>
                    <option value="quarantine">Quarantine</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Supplier Tab */}
          {activeTab === "supplier" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.supplierName}
                    onChange={(e) =>
                      handleChange("supplierName", e.target.value)
                    }
                    placeholder="e.g., ABC Suppliers Ltd."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Supplier Code
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.supplierCode}
                    onChange={(e) =>
                      handleChange("supplierCode", e.target.value)
                    }
                    placeholder="e.g., SUP-001"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Supplier Contact
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.supplierContact}
                  onChange={(e) =>
                    handleChange("supplierContact", e.target.value)
                  }
                  placeholder="e.g., john@supplier.com, +1234567890"
                />
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Description
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.itemDescription}
                  onChange={(e) =>
                    handleChange("itemDescription", e.target.value)
                  }
                  placeholder="Detailed description of the item, specifications, etc."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Storage Conditions
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.storageConditions}
                  onChange={(e) =>
                    handleChange("storageConditions", e.target.value)
                  }
                  placeholder="e.g., Store in cool, dry place. Temperature: 15-25°C"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Additional notes, special instructions, etc."
                  rows={3}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => navigate("/admin/inventory")}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Create Inventory Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
